import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Box,
  Typography,
  Slider,
  Snackbar,
  Alert,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import Cropper from "react-easy-crop";
import getCroppedImg from "./talent/components/cropUtils";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";

// Reads video duration without loading ffmpeg — used to estimate the
// source's average bitrate so we don't re-encode files that are
// already efficiently compressed (e.g. HEVC/H.265 sources).
const getVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const videoEl = document.createElement("video");
    videoEl.preload = "metadata";

    videoEl.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(videoEl.duration);
    };

    videoEl.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read video metadata"));
    };

    videoEl.src = url;
  });

// Average bitrate of the source file, in kbps
const estimateBitrateKbps = (file, durationSeconds) => {
  if (!durationSeconds || durationSeconds <= 0) return null;
  return Math.round((file.size * 8) / durationSeconds / 1000);
};
// ================== FFMPEG SETUP ==================
let ffmpeg = new FFmpeg();
let ffmpegLoaded = false;

const loadFFmpeg = async () => {
  if (!ffmpegLoaded) {
    await ffmpeg.load();
    ffmpegLoaded = true;
  }
};



// Kill the running encode immediately and force a fresh worker/core
// to be loaded next time compressVideo() is called.
const cancelCompression = () => {
  try {
    ffmpeg.terminate();
  } catch (e) {
    // already terminated / never started — ignore
  }
  // terminate() destroys the underlying worker, so the instance can't
  // be reused — recreate it and mark it as needing a reload.
  ffmpeg = new FFmpeg();
  ffmpegLoaded = false;
};

const compressVideo = async (file, onProgress) => {
  await loadFFmpeg();

  const input = `input.${file.name.split(".").pop()}`;
  const output = "output.mp4";

  await ffmpeg.writeFile(input, await fetchFile(file));

  ffmpeg.off("progress"); // clear previous listeners

  let lastReported = 0;

  ffmpeg.on("progress", ({ progress }) => {
    if (typeof progress !== "number" || Number.isNaN(progress)) return;

    // clamp to 0–1 range — ffmpeg.wasm can report negative or >1 values
    // when duration probing is off (common on larger/odd-metadata files)
    const clamped = Math.min(Math.max(progress, 0), 1);
    const pct = Math.round(clamped * 100);

    // ignore backward jumps (ffmpeg sometimes fires a stray low value
    // mid-encode on larger files when it starts an internal re-pass)
    if (pct < lastReported && pct !== 0) return;

    lastReported = pct;
    onProgress(pct);
  });

await ffmpeg.exec([
  "-i",
  input,
  "-vf",
  "scale='min(1280,iw)':-2",
  "-c:v",
  "libx264",
  "-preset",
  "veryfast",
  "-crf",
  "28",
  "-maxrate",
  "2500k",
  "-bufsize",
  "5000k",
  "-c:a",
  "aac",
  "-b:a",
  "128k",
  output,
]);

  const data = await ffmpeg.readFile(output);
  await ffmpeg.deleteFile(input);
  await ffmpeg.deleteFile(output);

  return new File([data.buffer], "compressed.mp4", {
    type: "video/mp4",
  });
};

const UploadMediaDialog = ({ open, onClose, userId, onUploadSuccess }) => {
  const [mediaType, setMediaType] = useState("");
  const [mediaTitle, setMediaTitle] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);

  // Toast
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Cropper
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [aspect, setAspect] = useState(3 / 4);

  // Cancel handling
  const abortControllerRef = useRef(null);
  const isCancelledRef = useRef(false);

  // Reset the cancelled flag whenever the dialog is freshly opened
  useEffect(() => {
    if (open) isCancelledRef.current = false;
  }, [open]);

  // Cleanup
  useEffect(() => {
    return () => {
      mediaFiles.forEach(
        (file) => file.previewUrl && URL.revokeObjectURL(file.previewUrl),
      );
      if (cropImage?.url) URL.revokeObjectURL(cropImage.url);
    };
  }, [mediaFiles, cropImage]);

  const resetForm = () => {
    setMediaTitle("");
    setMediaUrl("");
    setMediaFiles([]);
  };

  // Used for backdrop click / Esc — still blocks while an upload is in
  // flight so it can't be dismissed by accident. Use handleCancel for the
  // explicit Cancel button, which force-stops everything.
  const handleClose = () => {
    if (uploading) return;
    resetForm();
    onClose();
  };

  // Explicit Cancel button — actually stops compression and/or the
  // in-flight upload request instead of just hiding the dialog.
  const handleCancel = () => {
    isCancelledRef.current = true;

    if (isCompressing) {
      cancelCompression();
      setIsCompressing(false);
      setCompressionProgress(0);
    }

    if (uploading && abortControllerRef.current) {
      abortControllerRef.current.abort();
      setUploading(false);
    }

    resetForm();
    onClose();
  };

  // ---------------- VIDEO URL SUPPORT ----------------
  const validateVideoUrl = (url) => {
    const ytRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/(watch\?v=|embed\/|shorts\/)?[\w-]{11}/;
    const vimeoRegex = /^(https?:\/\/)?(www\.)?vimeo\.com\/\d+/;
    return ytRegex.test(url) || vimeoRegex.test(url);
  };

  const getEmbedUrl = (url) => {
    const ytMatch = url.match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/,
    );
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

    return "";
  };

  // ---------------- IMAGE DROP ----------------
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.type.startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      setMediaFiles([Object.assign(file, { previewUrl })]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  // ---------------- CROPPING ----------------
  const handleCropComplete = async () => {
    if (!cropImage) return;

    try {
      const { blob } = await getCroppedImg(
        cropImage.url,
        croppedAreaPixels,
        rotation,
      );

      const croppedFile = new File([blob], cropImage.file.name, {
        type: blob.type,
      });

      const previewUrl = URL.createObjectURL(croppedFile);

      setMediaFiles([Object.assign(croppedFile, { previewUrl })]);
    } catch (error) {
      console.error("Crop error:", error);
    } finally {
      URL.revokeObjectURL(cropImage.url);
      setCropImage(null);
      setCropModalOpen(false);
    }
  };

  // ---------------- UPLOAD ----------------
  const handleUpload = async () => {
    if (!mediaType) {
      setToast({
        open: true,
        message: "Please select a media type.",
        severity: "error",
      });
      return;
    }

    if (
      ["VIDEO", "YTvideo", "VIMvideo"].includes(mediaType) &&
      !mediaTitle.trim()
    ) {
      setToast({
        open: true,
        message: "Please enter a media title.",
        severity: "error",
      });
      return;
    }

    // Validation
    if (mediaType === "VIDEO" && mediaFiles.length === 0) {
      setToast({
        open: true,
        message: "Please select a video file.",
        severity: "error",
      });
      return;
    }

    if (["YTvideo", "VIMvideo"].includes(mediaType) && !mediaUrl.trim()) {
      setToast({
        open: true,
        message: "Please enter a video URL.",
        severity: "error",
      });
      return;
    }

    setUploading(true);
    isCancelledRef.current = false;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const uploads = [];

    if (["YTvideo", "VIMvideo"].includes(mediaType)) {
      const embedUrl = getEmbedUrl(mediaUrl);

      if (!validateVideoUrl(mediaUrl) || !embedUrl) {
        setToast({
          open: true,
          message: "Invalid video URL",
          severity: "error",
        });
        setUploading(false);
        return;
      }

      const form = new FormData();
      form.append("MediaId", 0);
      form.append("UserId", userId);
      form.append("MediaType", mediaType);
      form.append("MediaTitle", mediaTitle);
      form.append("PicSort", 0);
      form.append("ActiveFlag", true);
      form.append("UserBy", userId);
      form.append("MediaLink", embedUrl);
      form.append("MediaFile", "null");

      uploads.push(form);
    } else {
      mediaFiles.forEach((file) => {
        const form = new FormData();
        form.append("MediaId", 0);
        form.append("UserId", userId);
        form.append("MediaType", mediaType);
        form.append("MediaTitle", mediaTitle);
        form.append("PicSort", 0);
        form.append("ActiveFlag", true);
        form.append("UserBy", userId);
        form.append("MediaFile", file);
        form.append("MediaLink", "null");

        uploads.push(form);
      });
    }

    try {
      for (const formData of uploads) {
        const res = await api.post(ENDPOINTS.addOrUpdateUserMedia(), formData, {
          signal: controller.signal,
        });
        const result = res.data;

        if (res.status !== 200 || !(result.success || result.status === "Ok")) {
          throw new Error("Upload failed");
        }
      }

      if (isCancelledRef.current) return;

      setToast({
        open: true,
        message: "Media uploaded successfully!",
        severity: "success",
      });

      onUploadSuccess();
      handleClose();
    } catch (err) {
      if (
        err.name === "CanceledError" ||
        err.name === "AbortError" ||
        isCancelledRef.current
      ) {
        return; // user-initiated cancel — no error toast
      }

      console.error("Upload error:", err);
      setToast({
        open: true,
        message: "One or more uploads failed.",
        severity: "error",
      });
    } finally {
      if (!isCancelledRef.current) setUploading(false);
    }
  };

  return (
    <>
      {/* Toast */}
      <Snackbar
  open={toast.open}
  autoHideDuration={3000}
  onClose={() => setToast({ ...toast, open: false })}
  anchorOrigin={{ vertical: "top", horizontal: "center" }}
  sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
>
  <Alert
    severity={toast.severity}
    variant="filled"
    onClose={() => setToast({ ...toast, open: false })}
    sx={{ width: "100%" }}
  >
    {toast.message}
  </Alert>
</Snackbar>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Photo / Video / Audio</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {/* Media Type */}
            <Grid item xs={12}>
              <TextField
                select
                label="Media Type"
                fullWidth
                value={mediaType}
                onChange={(e) => {
                  resetForm();
                  setMediaType(e.target.value);
                }}
              >
                <MenuItem value="PICTURE">Image</MenuItem>
                <MenuItem value="AUDIO">Audio</MenuItem>
                <MenuItem value="VIDEO">Video File</MenuItem>
                <MenuItem value="YTvideo">YouTube Video</MenuItem>
                <MenuItem value="VIMvideo">Vimeo Video</MenuItem>
              </TextField>
            </Grid>

            {/* Video URL */}
            {["YTvideo", "VIMvideo"].includes(mediaType) && (
              <Grid item xs={12}>
                <TextField
                  label="Video URL"
                  fullWidth
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                />
              </Grid>
            )}

            {/* Title */}
            <Grid item xs={12}>
              <TextField
                label="Media Title"
                fullWidth
                required={["VIDEO", "YTvideo", "VIMvideo"].includes(mediaType)}
                value={mediaTitle}
                onChange={(e) => setMediaTitle(e.target.value)}
                disabled={mediaType === "PICTURE"}
              />
            </Grid>

            {/* IMAGE */}
            {mediaType === "PICTURE" && (
              <Grid item xs={12}>
                {!mediaFiles.length && (
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: "2px dashed #aaa",
                      borderRadius: 2,
                      p: 4,
                      textAlign: "center",
                      cursor: "pointer",
                      backgroundColor: isDragActive ? "#f0f7ff" : "#fafafa",
                    }}
                  >
                    <input {...getInputProps()} />
                    <Typography>
                      Drag & drop image here, or click to select
                    </Typography>
                  </Box>
                )}

                {mediaFiles.length > 0 && (
                  <Box
                    mt={2}
                    textAlign="center"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={mediaFiles[0].previewUrl}
                      alt="preview"
                      style={{ width: 220, borderRadius: 8 }}
                    />

                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1.5 }}
                      onClick={() => {
                        const file = mediaFiles[0];
                        const url = URL.createObjectURL(file);
                        setCropImage({ file, url });
                        setCropModalOpen(true);
                      }}
                    >
                      Crop Image
                    </Button>
                  </Box>
                )}
              </Grid>
            )}

            {/* AUDIO */}
            {mediaType === "AUDIO" && (
              <Grid item xs={12}>
                <Button variant="contained" component="label" fullWidth>
                  Choose Audio File
                  <input
                    type="file"
                    hidden
                    accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,.wma"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;

                      if (!file.type.startsWith("audio/")) {
                        setToast({
                          open: true,
                          message: "Please select a valid audio file.",
                          severity: "error",
                        });
                        e.target.value = "";
                        return;
                      }

                      const previewUrl = URL.createObjectURL(file);
                      setMediaFiles([Object.assign(file, { previewUrl })]);
                    }}
                  />
                </Button>
                <Typography
                  variant="caption"
                  sx={{ mt: 1, display: "block", color: "#666" }}
                >
                  Supported formats: MP3, Wav, m4a, aac, ogg, flac, wma
                </Typography>

                {mediaFiles.length > 0 && (
                  <Box mt={2}>
                    <audio controls style={{ width: "100%" }}>
                      <source src={mediaFiles[0].previewUrl} />
                    </audio>
                    <Typography variant="caption">
                      {mediaFiles[0].name}
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}

            {/* VIDEO FILE */}
            {mediaType === "VIDEO" && (
              <Grid item xs={12}>
                <Button variant="contained" component="label" fullWidth>
                  Choose Video File
                  <input
                    type="file"
                    hidden
                    accept=".mp4,.mov,.avi,.wmv,.webm,.mkv,.m4v"
                  onChange={async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  // Validate that only video files are selected
  if (!file.type.startsWith("video/")) {
    setToast({
      open: true,
      message: "Please select a valid video file.",
      severity: "error",
    });
    e.target.value = "";
    return;
  }

  const MAX_SIZE_MB = 500;

  // Validate size
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    setToast({
      open: true,
      message: "Video must be under 500MB",
      severity: "error",
    });
    return;
  }

  try {
    const isWebM =
      file.type.includes("webm") ||
      file.name.toLowerCase().endsWith(".webm");

    // Small MP4 files don't need compression
    if (file.size < 20 * 1024 * 1024 && !isWebM) {
      const previewUrl = URL.createObjectURL(file);
      setMediaFiles([Object.assign(file, { previewUrl })]);
      return;
    }

    // Probe the source's own average bitrate — if it's already at or
    // below our target ceiling, re-encoding would only inflate it
    // (common with efficient HEVC/H.265 sources), so skip compression.
    const TARGET_MAXRATE_KBPS = 2500;

    let sourceBitrateKbps = null;
    try {
      const duration = await getVideoDuration(file);
      sourceBitrateKbps = estimateBitrateKbps(file, duration);
    } catch (probeErr) {
      // Metadata probe failed (unusual container, etc.) — fall through
      // to compression as before; ffmpeg will handle it directly.
      console.warn("Bitrate probe failed, proceeding to compress:", probeErr);
    }

    if (sourceBitrateKbps !== null && sourceBitrateKbps <= TARGET_MAXRATE_KBPS) {
      const previewUrl = URL.createObjectURL(file);
      setMediaFiles([Object.assign(file, { previewUrl })]);
      setToast({
        open: true,
        message: "Video is already efficiently encoded — using original file.",
        severity: "success",
      });
      return;
    }

    // Show optimization message
    if (file.size > 50 * 1024 * 1024 || isWebM) {
      setToast({
        open: true,
        message: "Optimizing video for playback...",
        severity: "info",
      });
    }

    setIsCompressing(true);
    setCompressionProgress(0);

    const compressed = await compressVideo(
      file,
      setCompressionProgress,
    );

    // Dialog was cancelled mid-compression — drop the
    // result, don't touch state for a new attempt.
    if (isCancelledRef.current) return;

    const previewUrl = URL.createObjectURL(compressed);
    setMediaFiles([
      Object.assign(compressed, { previewUrl }),
    ]);

    setToast({
      open: true,
      message: "Video optimized for smooth playback",
      severity: "success",
    });
  } catch (err) {
    if (isCancelledRef.current) return; // expected — we killed it ourselves

    console.error(err);

    // Fall back to original file
    const previewUrl = URL.createObjectURL(file);
    setMediaFiles([Object.assign(file, { previewUrl })]);

    setToast({
      open: true,
      message: "Compression failed, using original file",
      severity: "warning",
    });
  } finally {
    if (!isCancelledRef.current) {
      setIsCompressing(false);
      setCompressionProgress(0);
    }
  }
}}
                  />
                </Button>

                {isCompressing && (
                  <Box mt={2}>
                    <Typography variant="caption">
                      {compressionProgress === 0
                        ? "Preparing video optimizer..."
                        : `Optimizing video... ${Math.min(Math.max(compressionProgress, 0), 100)}%`}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(Math.max(compressionProgress, 0), 100)}
                    />
                  </Box>
                )}

                <Typography
                  variant="caption"
                  sx={{ mt: 1, display: "block", color: "#666" }}
                >
                  Supported formats: MP4, WebM, MOV, AVI • Max size: 500MB
                  <br />
                  Best: 720p–1080p, 2–5 Mbps bitrate, under 3 mins
                </Typography>

                {mediaFiles.length > 0 && (
  <video
    controls
    width="260"
    style={{
      marginTop: 10,
      borderRadius: 8,
      maxHeight: 400,
      maxWidth: "100%",
      objectFit: "contain",
      display: "block",
    }}
    src={mediaFiles[0].previewUrl}
  />
)}
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || isCompressing}
            startIcon={
              uploading || isCompressing ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CROP MODAL */}
      <Dialog open={cropModalOpen} maxWidth={false}>
        <DialogTitle>Crop & Rotate</DialogTitle>

        <DialogContent sx={{ px: 3 }}>
          <Box
            sx={{
              position: "relative",
              width: "40vw",
              maxHeight: "55vh",
              aspectRatio: aspect,
              backgroundColor: "#000",
              borderRadius: 2,
            }}
          >
            <Cropper
              image={cropImage?.url}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={(_, area) => setCroppedAreaPixels(area)}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography>Aspect Ratio</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={aspect}
              onChange={(e) => setAspect(parseFloat(e.target.value))}
            >
              <MenuItem value={1 / 1}>1:1</MenuItem>
              <MenuItem value={3 / 4}>3:4</MenuItem>
              <MenuItem value={4 / 3}>4:3</MenuItem>
              <MenuItem value={16 / 9}>16:9</MenuItem>
            </TextField>

            <Typography sx={{ mt: 2 }}>Zoom</Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, v) => setZoom(v)}
            />

            <Typography>Rotate</Typography>
            <Slider
              value={rotation}
              min={0}
              max={360}
              step={1}
              onChange={(_, v) => setRotation(v)}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setCropModalOpen(false)}>Cancel</Button>
          <Button onClick={handleCropComplete} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadMediaDialog;
